// Apolo Loop Engineering — STEP POLICY (decide next). PURE.
//
// The loop decides the next step via a policy, not the prose. A policy is a pure fn:
// (state, ctx) => { action:'continue'|'stop'|'await_human' }.
//   ctx = { lastVerdict, iterations, maxIterations, aborted, overBudget, approved }

/**
 * Default policy = reproduce today's runLocalAgentLoop control flow: stop on abort,
 * over-budget, or maxIterations; stop once the objective gate passed; otherwise continue.
 */
export function defaultPolicy(_state, ctx = {}) {
  if (ctx.aborted) return { action: 'stop', reason: 'aborted' };
  if (ctx.overBudget) return { action: 'stop', reason: 'over_budget' };
  if (typeof ctx.maxIterations === 'number' && ctx.iterations >= ctx.maxIterations) {
    return { action: 'stop', reason: 'max_iterations' };
  }
  if (ctx.lastVerdict?.verdict === 'pass') return { action: 'stop', reason: 'gate_pass' };
  return { action: 'continue' };
}

/**
 * await-human policy = the Planning Mode gate: pause for approval before executing.
 * When `approved` is false/absent it yields 'await_human'; once approved it defers to
 * the default policy (so abort/budget/gate still apply during execution).
 */
export function awaitHumanPolicy(state, ctx = {}) {
  if (!ctx.approved) return { action: 'await_human', reason: 'needs_approval' };
  return defaultPolicy(state, ctx);
}

/**
 * Spine iteration-boundary policy (E4-WS1-T02): decides whether the for-loop
 * should CONTINUE or STOP before the next LLM call. This is the FC-2 interface
 * `stepPolicy.next` for the live spine — same shape as defaultPolicy but
 * specialized to the spine's signal/abort/budget/maxIterations semantics.
 *
 * @param {object} _s      - SpineState (unused now; reserved for gate-on-state)
 * @param {{ aborted?: boolean, iteration?: number, maxIterations?: number }} [ctx]
 * @returns {{ action: 'continue'|'stop', reason?: string }}
 */
export function spinePolicy(_s, ctx) {
  ctx = ctx || {};
  if (ctx.aborted) return { action: 'stop', reason: 'aborted' };
  if (typeof ctx.maxIterations === 'number' && ctx.iteration >= ctx.maxIterations) {
    return { action: 'stop', reason: 'max_iterations' };
  }
  return { action: 'continue' };
}

export const POLICIES = {
  default: defaultPolicy,
  await_human: awaitHumanPolicy,
  spine: spinePolicy,
};

export function resolvePolicy(name) {
  return POLICIES[name] || defaultPolicy;
}
